import { DateFormatPipe } from 'app/pipes/date-format.pipe';

export enum enumImportDataType{
   InCaseData = 0,
   JSON = 1,
}

export enum enumTPDataType { None, Boolean, Category, Date, Lookup, Number, Markers, Memo, Status, Text, User }

export class ParsedImportData{
   dataType: enumImportDataType;
   subjects: TPSubject[];
   error: string;
   rawData: string;
   jsonData: string;
   categories: MappingItem[];
   statuses: MappingItem[];
   types: MappingItem[];
   informationSources: MappingItem[];
   users: MappingItem[];
   relationships: MappingItem[];
   counts: Map<string, number>;
   source: string;
   useWizard: boolean;

   constructor(data?){
      data = data || {};
      this.dataType = data.dataType;
      this.subjects = data.subjects;
      this.error = data.error;
      this.rawData = data.rawData;
      this.jsonData = data.jsonData;
      this.categories = data.categories || [];
      this.statuses = data.statuses || [];
      this.types = data.types || [];
      this.informationSources = data.informationSources || [];
      this.users = data.users || [];
      this.relationships = data.relationships || [];
      this.counts = data.counts || new Map();
      this.source = data.source || '';
      this.useWizard = (data.useWizard === false) ? false : true;
   }

   public get hasFileInfo(): boolean{
      if (this.subjects){
         try{
            const subjects = this.subjects.filter((subject) => subject.import);
            return (subjects.some((x) => x.Files.length > 0)) ? true : false; 
         }
         catch (e){
           return false;
         }
      }
      return false;
   }

   public toJSON(): string{
      const tpData: ThirdPartyData = JSON.parse(this.jsonData);

      // build map of first instance of all subjects
      const uniqueSubjects: Map<string, TPSubject> = this.getSubjects(null, true);

      // search subjects, replacing mapped value with new value
      if (tpData.Subjects && this.subjects){
         tpData.Subjects = this.subjects.filter((subject) => subject.import);
         if (tpData.Subjects.length > 0){
          
          // iterate through subjects which we are going to import, replacing the first found instance with the first instance from our map
          const replacedSubjectSet: Set<string> = new Set();
          tpData.Subjects = this.updateSubjects(tpData.Subjects, uniqueSubjects, replacedSubjectSet);

          // and now apply mapping to subjects
          tpData.Subjects.forEach((subject) => {
              this.applyMappingToSubject(subject);
          });
         }
      }

      // and return tpData as string
      return JSON.stringify(tpData, null, 2);
   }

   private getMappedValue(mappingItems: MappingItem[], originalValue: string, category?: string): string{
      const item = mappingItems.find((i) => ((!originalValue) || (i.originalValue === originalValue)) && ((!category) || (i.originalCategory === category)));
      return item ? item.mappedValue : originalValue;
   }

   private setMappedDataField(mappingItems: MappingItem[], subject, name: string, value: string, label: string, type: string){
      let field = subject.DataFields.find((i) => i.Name.toLowerCase() === name.toLowerCase());
      if (!field){
         field = new TPDataField({
            Name: name,
            Value: this.getMappedValue(mappingItems, value, subject.Category),
            Label: label,
            Type: type,
         });
         subject.DataFields.push(field);
      }
      if (field.Value){
        field.Value = this.getMappedValue(mappingItems, field.Value, subject.Category);
      }
   }

   private updateSubjects(subjects: TPSubject[], uniqueSubjects: Map<string, TPSubject>, replacedSubjectSet: Set<string>){
      const updatedSubjects = [];
      if (subjects.length > 0){
       subjects.forEach((subject) => {
         const key = subject.Uid;
         let currentSubject = subject;
         if (!replacedSubjectSet.has(key)){
          if (uniqueSubjects.has(key)){
            currentSubject = uniqueSubjects.get(key);
            replacedSubjectSet.add(key);
          }
          if (currentSubject.LinkedSubjects.length > 0){
            currentSubject.LinkedSubjects = this.updateSubjects(currentSubject.LinkedSubjects, uniqueSubjects, replacedSubjectSet);
          }
         }
         updatedSubjects.push(currentSubject);
       });
      }
      return updatedSubjects;
   }

   private applyMappingToSubject(subject, parent?){

      // it follows that if we are only extracting mapping properties for subjects of type 'case'
      // then we only need to apply mapping to subjects of type 'case'
      if (subject.Type.toLowerCase() === 'case'){

         this.setMappedDataField(this.categories, subject, 'Category', subject.Category, 'Category', 'Category');
         this.setMappedDataField(this.statuses, subject, 'Status', '', 'Status', 'Status');
         this.setMappedDataField(this.types, subject, 'Type', '', 'Type', 'Lookup');
         this.setMappedDataField(this.informationSources, subject, 'InformationSource', '', 'Information Source', 'Lookup');
         
         subject.User = this.getMappedValue(this.users, subject.User, subject.Category);
         subject.Manager = this.getMappedValue(this.users, subject.Manager, subject.Category);

         // update the category last as we are using subject.Category to get the mapped values for the other properties
         subject.Category = this.getMappedValue(this.categories, subject.Category, subject.Category);
      }

      if (subject.RelationshipToParent){
        subject.RelationshipToParent = this.getMappedValue(this.relationships, subject.RelationshipToParent, this.getRelationshipType(subject, parent));
      }

      if (subject.LinkedSubjects && (subject.LinkedSubjects.length > 0)){
         subject.LinkedSubjects.forEach((s) => {
            this.applyMappingToSubject(s, subject);
         });
      }
   }

   public extractInfo(){
      if (this.subjects){
        this.clearMapping();

        let subjects = this.subjects.filter((subject) => subject.import);
        if (subjects.length > 0){

          // flesh out subjects based on selection
          const uniqueSubjects: Map<string, TPSubject> = this.getSubjects(null, true);
          const replacedSubjectSet: Set<string> = new Set();
          subjects = this.updateSubjects(subjects, uniqueSubjects, replacedSubjectSet);

          subjects.forEach((subject) => {
              this.extractInfoFromSubject(subject);
          });
        }

        // remove duplicate entries...
        this.categories = this.sortAndRemoveDuplicateAndEmptyValues(this.categories);
        this.statuses = this.sortAndRemoveDuplicateAndEmptyValues(this.statuses);
        this.types = this.sortAndRemoveDuplicateAndEmptyValues(this.types);
        this.informationSources = this.sortAndRemoveDuplicateAndEmptyValues(this.informationSources);
        this.users = this.sortAndRemoveDuplicateAndEmptyValues(this.users);
        this.relationships = this.sortAndRemoveDuplicateAndEmptyValues(this.relationships);
      }
   }

   private clearMapping(){
    this.counts = new Map();
    this.categories = [];
    this.statuses = [];
    this.types = [];
    this.informationSources = [];
    this.users = [];
    this.relationships = [];
   }

   private sortAndRemoveDuplicateAndEmptyValues(mappingItems: MappingItem[]): MappingItem[]{
      const uniqueMap = new Map<string, MappingItem>();
      mappingItems.map((x) => uniqueMap.set(x.originalValue + x.originalCategory, x));
      return Array.from(uniqueMap.values()).filter((i) => i.originalCategory)
      .sort((a, b) => a.originalValue.localeCompare(b.originalValue) || a.originalCategory.localeCompare(b.originalCategory));
   }

   private extractInfoFromSubject(subject: TPSubject, parent?: TPSubject){
      const currentCount = this.counts.has(subject.Type) ? this.counts.get(subject.Type) : 0;
      this.counts.set(subject.Type, currentCount + 1);
      
      // the mapped properties are only relevant to a case, so save ourselves some work by checking type first
      if (subject.Type.toLowerCase() === 'case'){
         const category = subject.Category || '';
         const user = subject.User || '';
         const manager = subject.Manager || '';
         const statusDataField = subject.DataFields.find((i) => i.Type.toLowerCase() === 'status');
         const typeDataField = subject.DataFields.find((i) => i.Name.toLowerCase() === 'type');
         const informationSourceDataField = subject.DataFields.find((i) => i.Name.toLowerCase() === 'informationsource');
         const statusValue = statusDataField ? statusDataField.Value : '';
         const typeValue = typeDataField ? typeDataField.Value : '';
         const infoSourceValue = informationSourceDataField ? informationSourceDataField.Value : '';
         this.categories.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: category, mappedValue: category}));
         this.statuses.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: statusValue, mappedValue: statusValue}));
         this.types.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: typeValue, mappedValue: typeValue}));
         this.informationSources.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: infoSourceValue, mappedValue: infoSourceValue}));
         this.users.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: user, mappedValue: user}));
         this.users.push(new MappingItem({originalCategory: category, mappedCategory: category, originalValue: manager, mappedValue: manager}));
      }

      if (subject.RelationshipToParent){
        const relationshipType = this.getRelationshipType(subject, parent);
        this.relationships.push(new MappingItem({originalCategory: relationshipType, originalValue: subject.RelationshipToParent, mappedValue: subject.RelationshipToParent}));
      }

      if (subject.LinkedSubjects && (subject.LinkedSubjects.length > 0)){
         subject.LinkedSubjects.forEach((s) => {
            this.extractInfoFromSubject(s, subject);
         });
      }
   }

   private getRelationshipType(subject, parent?){
     return (parent) ? `${parent.Type}/${subject.Type}` : undefined;
   }

   public syncMappedCategories(){
      this.updateMappedCategories(this.informationSources);
      this.updateMappedCategories(this.statuses);
      this.updateMappedCategories(this.types);
      this.updateMappedCategories(this.users);
   }

   private updateMappedCategories(prop){
      prop.forEach((i) => {
         const category = this.categories.find((x) => x.originalCategory === i.originalCategory);
         if (category){
            i.mappedCategory = category.mappedCategory;
         }
      });
   }

   public getSubjects(subject?: TPSubject, allSubjects?: boolean): Map<string, TPSubject>{
     const uniqueSubjects = new Map<string, TPSubject>();
     const subjects = (subject) ? subject.LinkedSubjects : (allSubjects ? this.subjects : this.subjects.filter((s) => s.import));
     if (subject && (!uniqueSubjects.has(subject.Uid))){
        uniqueSubjects.set(subject.Uid, subject);
     }
     subjects.forEach((s) => {
      const linkedSubjects = this.getSubjects(s);
      if (linkedSubjects.size > 0){
        linkedSubjects.forEach((linkedSubject) => {
          if (!uniqueSubjects.has(linkedSubject.Uid)){
            uniqueSubjects.set(linkedSubject.Uid, linkedSubject);
          }
        });
      }
     });
     return uniqueSubjects;
   }

   public updateSubjectUid(oldUid, newUid, subjects?){
    subjects = subjects || this.subjects;
    subjects.forEach((subject) => {
      if (subject.Uid === oldUid){
        subject.Uid = newUid;
      }
      if (subject.LinkedSubjects && (subject.LinkedSubjects.length > 0)){
        this.updateSubjectUid(oldUid, newUid, subject.LinkedSubjects);
      }
    });
   }
}

// tslint:disable-next-line: max-classes-per-file
export class ImportOptions{
   dataType: enumImportDataType;
   currentUserName: string;
   directLinkDepth: number;
   existingSubjects: string;
   filesPath: string;
   files: any;
   matching: string;
   subjectNote: string;
   source: string;

   constructor(options?){
      options = options || {};
      this.dataType = options.dataType;
      this.currentUserName = options.currentUserName;
      this.directLinkDepth = options.directLinkDepth || 2;
      this.existingSubjects = options.existingSubjects;
      this.filesPath = options.filesPath;
      this.files = options.files;
      this.source = options.source || '';
      this.matching = options.matching || this.getDefaultMatching(this.dataType);
      this.subjectNote = options.subjectNote || this.getDefaultSubjectNote(this.dataType, this.source, this.currentUserName);
   }

   setDefaultSubjectNote(dataType?: enumImportDataType, source?, currentUserName?){
      dataType = dataType || this.dataType;
      currentUserName = currentUserName || this.currentUserName;
      source = source || '';
      this.subjectNote = this.getDefaultSubjectNote(dataType, source, currentUserName);
   }

   getDefaultSubjectNote(dataType: enumImportDataType, source, currentUserName){
      dataType = dataType || this.dataType;
      currentUserName = currentUserName || this.currentUserName;
      let product = source;
      if (!product){
        switch (dataType){
          case enumImportDataType.InCaseData:
              product = 'IDIS';
              break;
          case enumImportDataType.JSON:
              product = 'InCase Intelligence';
              break;
        }
      }
      if (product){
         const currentDate = new DateFormatPipe('en-GB').transform(new Date(Date.now()));
         return `Imported from ${product} on ${currentDate}` + (currentUserName ? ` by ${currentUserName}` : '');
      }
      return '';
   }

   getDefaultMatching(dataType: enumImportDataType){
    switch (dataType){
      case enumImportDataType.InCaseData:
         return 'Review';
      case enumImportDataType.JSON:
         return 'Ignore'; // keep the Uid and import as if new
    }
   }
}

// tslint:disable-next-line: max-classes-per-file
export class MappingItem{
   originalCategory: string;
   mappedCategory: string;
   originalValue: string;
   mappedValue: string;
   availableValues: string[];

   constructor(item?){
      item = item || {};
      this.originalCategory = item.originalCategory;
      this.mappedCategory = item.mappedCategory;
      this.originalValue = item.originalValue;
      this.mappedValue = item.mappedValue;
      this.availableValues = item.availableValues || [];
   }
}

// tslint:disable-next-line: max-classes-per-file
export class ThirdPartyData{
    Subjects: TPSubject[];
    Categories: [];
    User: [];
    MarkAsArchived: boolean;
    Source: string;
    UseWizard: boolean;

    constructor(data?){
       data = data || {};
       this.Subjects = data.Subjects || [];
       this.Categories = data.Categories || [];
       this.User = data.User || [];
       this.MarkAsArchived = data.MarkAsArchived || false;
       this.Source = data.Source || '';
       this.UseWizard = (data.UseWizard === false) ? false : true;
    }
}

// tslint:disable-next-line: max-classes-per-file
export class TPSubject{
   ID: number;
   Uid: string;
   RelationshipToParent: string;
   HideRelationship: string;
   Reference: string;
   Category: string;
   DataFields: TPDataField[];
   LinkedSubjects: TPSubject[];
   Processes: [];
   Tasks: [];
   Notes: any[];
   Type: string;
   User: string;
   Manager: string;
   RestrictedUsers: string;
   Files: any[];
   URLs: any[];
   additionalNotes: any[];
   import: boolean;
   
   constructor(subject?){
      subject = subject || {};
      this.ID = subject.ID;
      this.Uid = subject.Uid;
      this.RelationshipToParent = subject.RelationshipToParent;
      this.HideRelationship = subject.HideRelationship;
      this.Reference = subject.Reference;
      this.Category = subject.Category;
      this.DataFields = subject.DataFields || [];
      this.LinkedSubjects = subject.LinkedSubjects || [];
      this.Processes = subject.Processes || [];
      this.Tasks = subject.Tasks || [];
      this.Notes = subject.Notes || [];
      this.Type = subject.Type;
      this.User = subject.User;
      this.Manager = subject.Manager;
      this.RestrictedUsers = subject.RestrictedUsers;
      this.Files = subject.Files || [];
      this.URLs = subject.URLs || [];
      this.additionalNotes = subject.additionalNotes || [];
      this.import = subject.import || true;
   }
}

// tslint:disable-next-line: max-classes-per-file
export class TPDataField{
   Name: string;
   Value: string;
   Label: string;
   Required: string;
   HideRecord: string;
   Type: string;

   constructor(field?){
      field = field || {};
      this.Name = field.Name;
      this.Value = field.Value;
      this.Label = field.Label;
      this.Required = field.Required || false,
      this.HideRecord = field.HideRecord || false,
      this.Type = field.Type;
   }
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectMatch{
  uid: string;
  subjectType: string;
  reference: string;
  matchedSubjects: any[];
  matchedSubjectUid: string;
  matchedSubjectReference: string;
  useExisting: boolean;
  alreadyExists: boolean;
  subject: TPSubject;

  constructor(match?){
    match = match || {};
    this.uid = match.uid;
    this.subjectType = match.subjectType;
    this.reference  = match.reference;
    this.matchedSubjects = match.matchedSubjects || [];
    this.matchedSubjectUid = match.matchedSubjectUid;
    this.matchedSubjectReference = match.matchedSubjectReference;
    this.useExisting = match.useExisting || false;
    this.alreadyExists = match.alreadyExists || false;
    this.subject = match.subject;
  }
}
